import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import { useContent } from "@/lib/content-context";

const HERO = "https://images.pexels.com/photos/3935702/pexels-photo-3935702.jpeg?auto=compress&cs=tinysrgb&w=1800&q=80";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { services } = useContent();
  const s = services.find((x) => x.slug === slug);
  if (!s) return <Navigate to="/services" replace />;

  const idx = services.findIndex((x) => x.slug === slug);
  const next = services[(idx + 1) % services.length];

  return (
    <div data-testid={`service-page-${s.slug}`}>
      <section className="relative h-[55vh] min-h-[400px] -mt-24 flex items-end overflow-hidden">
        <img src={HERO} alt={s.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight to-midnight/30" />
        <div className="relative container-edge pb-14 text-linen">
          <Link to="/services" className="inline-flex items-center gap-2 text-linen/70 hover:text-ochre text-xs tracking-widest uppercase mb-6">
            <ArrowLeft className="w-4 h-4" /> All services
          </Link>
          <div className="overline text-ochre mb-4">Service · {String(idx + 1).padStart(2, "0")}</div>
          <h1 className="editorial-h1 max-w-3xl">{s.name}.</h1>
        </div>
      </section>

      <section className="section-pad bg-linen">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <Reveal className="md:col-span-5">
            <div className="overline text-terracotta mb-4">Overview</div>
            <p className="text-2xl font-serif text-ink/85 leading-snug">{s.short}</p>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7">
            <p className="text-lg text-ink/75 leading-relaxed font-light">{s.long}</p>
          </Reveal>
        </div>
      </section>

      <section className="section-pad bg-sand">
        <div className="container-edge">
          <SectionHeading overline="Deliverables" title="What's included." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
            {s.deliverables.map((d) => (
              <div key={d} className="bg-linen p-7 flex items-start gap-4">
                <Check className="w-5 h-5 stroke-1 text-terracotta mt-1 flex-shrink-0" />
                <span className="text-base leading-relaxed">{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-midnight">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5 text-linen">
            <div className="overline text-ochre mb-4">Next steps</div>
            <h2 className="editorial-h2 text-linen">Talk to our team.</h2>
            <p className="mt-6 text-linen/75 leading-relaxed">
              Whether you are scoping a UK launch or considering changing GSA partners, we'd welcome a confidential conversation about how Simba Travel's {s.name.toLowerCase()} capability would support your goals.
            </p>
            <Link to={`/services/${next.slug}`} className="btn-secondary border-linen/40 text-linen hover:bg-linen hover:text-midnight mt-10" data-testid="service-next">
              Next: {next.name} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="md:col-span-7">
            <EnquiryForm
              defaultType="airline-partnership"
              title={`${s.name} enquiry`}
              subtitle="Tell us a little about your airline and your requirements. A senior team member will follow up directly."
              hiddenContext={{ subject: `Service enquiry: ${s.name}` }}
              variant="dark"
              testIdPrefix={`service-${s.slug}-enquiry`}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
